import { bridge, localStorageBridge } from '../../utils';
import { useCreateLikeFolderBottomSheet } from './hooks/useCreateLikeFolderBottomSheet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { likeGoodsService } from '../../api';
import { useCreateNewLikeFolderBottomSheet } from './hooks/useCreateNewLikeFolderBottomSheet';
import { systemAlert } from '../../component';
import { toast } from './toast';
import { queryKey } from '../../constant';
import type { GetMemberInfoResponse } from '../../api/getMemberInfo/types';
import { useShowAnonymousLikeCountLimitAlert } from './hooks/useShowAnonymousLikeCountLimitAlert';
import { useMyInfo } from '../../queries';
import type { LogAnalyticsEventParams } from '../../utils/bridge/logAnalyticsEvent/types';
import { useCallback } from 'react';

const STORAGE_KEY = 'isShownCreateLikeFolder';

type UseAddLikeGoodsProps = {
  listParams?: string;
  onChangeIsLiked: (sno: number, nextValue: boolean) => void;
  screen: string;
};




export const useAddLikeGoods = ({
  onChangeIsLiked,
  listParams,
  screen,
}: UseAddLikeGoodsProps) => {
  const { saveGoodsToDefaultLikeFolder } = useSaveGoodsToDefaultLikeFolder(
    onChangeIsLiked,
    screen,
    listParams,
  );
  const { show: showAnonymousLikeCountLimitAlert } =
    useShowAnonymousLikeCountLimitAlert();

  const { data: originMemberInfo, refetch } = useMyInfo();

  const { show } = useCreateLikeFolderBottomSheet();
  const { show: createNewLikeFolderBottomSheet } =
    useCreateNewLikeFolderBottomSheet();
  /**
   * - 조건에 따라 알럿 또는 바텀시트가 나온다.
   */
  const addLikeGoods = useCallback(
    async (
      sno: number,
      analyticsParams?: LogAnalyticsEventParams['analytics'],
      ablyOnlyLog?: LogAnalyticsEventParams['ably_only_log'],
    ) => {
      const getMemberInfo = async () => {
        if (!originMemberInfo) {
          const reFetchedMemberInfo = await refetch().then(
            result => result.data,
          );

          return reFetchedMemberInfo ?? null;
        }

        if (originMemberInfo.is_anonymous) {
          const reFetchedMemberInfo = await refetch().then(
            result => result.data,
          );

          return reFetchedMemberInfo ?? originMemberInfo;
        }
        return originMemberInfo;
      };
      const memberInfo = await getMemberInfo();
      if (!memberInfo) return;

      const { like_folders_count, is_anonymous } = memberInfo;
      const isChecked =
        (await localStorageBridge.getBoolean(STORAGE_KEY)) ?? false;

      const handleCondition = (() => {
        if (is_anonymous) {
          if (memberInfo.likes_count >= 5) {
            // 비회원이 찜 5개 찼을 때
            return 'anonymousLikeLimit';
          }
          return 'justSaveDefaultLikeFolder';
        }

        if (isChecked === false && like_folders_count < 2) {
          return 'showTutorialAlert';
        }
        if (like_folders_count > 1) {
          return 'showCreateNewLikeFolder';
        }
        if (like_folders_count === 1) {
          return 'justSaveDefaultLikeFolder';
        }
      })();

      if (handleCondition === 'anonymousLikeLimit') {
        bridge.logAnalyticsEvent({
          name: 'VIEW_POPUP_LIKE_GOODS_LIMIT',
          analytics: analyticsParams,
          ably_only_log: ablyOnlyLog,
        });

        showAnonymousLikeCountLimitAlert().then(res => {
          if (res.type === 'confirm') {
            bridge.logAnalyticsEvent({
              name: 'CLICK_LIKE_POPUP_SIGN_UP',
              analytics: analyticsParams,
              ably_only_log: ablyOnlyLog,
            });
            bridge.assertLogin({});
          }
        });
        return;
      }

      if (handleCondition === 'showTutorialAlert') {
        systemAlert(
          '서랍 만들기',
          '서랍은 찜을 담아두는 새로운 공간이에요. 찜한 상품을 새로운 서랍에 넣어볼까요?',
          [
            {
              text: '다음에요',
              onPress: () => {
                localStorageBridge.putBoolean(STORAGE_KEY, true);
                saveGoodsToDefaultLikeFolder(sno);
              },
            },
            {
              text: '네',
              onPress: async () => {
                localStorageBridge.putBoolean(STORAGE_KEY, true);
                const result = await show({
                  goodsSno: sno,
                  onChangeIsLiked,
                  screen,
                });
                if (result.type === 'cancel') {
                  return;
                }
                const { showCreateNewLikeFolder } = result.data;
                if (showCreateNewLikeFolder) {
                  createNewLikeFolderBottomSheet({
                    goodsSno: sno,
                    listParams: listParams,
                    screen,
                  }).then(result => {
                    if (result.type === 'confirm') {
                      onChangeIsLiked(result.data.goodsSno, true);
                    }
                  });
                }
              },
            },
          ],
        );
        return;
      }
      if (handleCondition === 'showCreateNewLikeFolder') {
        const result = await show({
          goodsSno: sno,
          onChangeIsLiked,
          screen,
        });
        if (result.type === 'cancel') {
          return;
        }
        const { showCreateNewLikeFolder } = result.data;
        if (showCreateNewLikeFolder) {
          createNewLikeFolderBottomSheet({
            goodsSno: sno,
            listParams,
            screen,
          }).then(result => {
            if (result.type === 'confirm') {
              onChangeIsLiked(result.data.goodsSno, true);
            }
          });
        }
        return;
      }
      if (handleCondition === 'justSaveDefaultLikeFolder') {
        saveGoodsToDefaultLikeFolder(sno);
        return;
      }
    },
    [
      originMemberInfo,
      refetch,
      showAnonymousLikeCountLimitAlert,
      saveGoodsToDefaultLikeFolder,
      show,
      onChangeIsLiked,
      screen,
      createNewLikeFolderBottomSheet,
      listParams,
    ],
  );

  return {
    addLikeGoods,
  };
};

const useSaveGoodsToDefaultLikeFolder = (
  onChangeIsLiked: (sno: number, nextValue: boolean) => void,
  screen: string,
  listParams?: string,
) => {
  const client = useQueryClient();

  const { mutate: mutateLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    onSuccess: async ({ data }, { folder_sno, sno }) => {
      onChangeIsLiked(sno, true);
      toast({ folderName: '기본 서랍', folderSno: folder_sno, screen });
      // queryKey.getMemberInfoQuery 갱신하기

      client.setQueryData<GetMemberInfoResponse>(
        queryKey.getMemberInfoQuery,
        oldData => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            likes_count: data.likes_count,
          };
        },
      );

      bridge.likeGoods({
        goods_sno: sno,
        is_liked: true,
        folder_sno,
      });
    },
  });

  const saveGoodsToDefaultLikeFolder = async (sno: number) => {
    mutateLikeGoods({
      list_params_referrer: listParams,
      sno,
      folder_sno: 0,
    });
  };
  return { saveGoodsToDefaultLikeFolder };
};
